import React, { useState } from 'react';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import { CalendarEvent, CurrencyRupee, ArrowRight } from 'react-bootstrap-icons';

const AllEvents = () => {

    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();
    const isDark = skin === 'dark';

    const eventCards = [
        {
            title: 'Automation events',
            tag: 'Activate / deactivate',
            description: 'Schedule activate or deactivate actions for products, categories, outlets, and advertisements.',
            icon: CalendarEvent,
            accent: '#4f46e5',
            accentSoft: isDark ? '#1f2350' : '#eef0ff',
            route: '/events/automation-events',
        },
        {
            title: 'Pricing update events',
            tag: 'Bulk price changes',
            description: 'Schedule price increases or decreases for menu groups by percentage or a fixed amount.',
            icon: CurrencyRupee,
            accent: '#0d9488',
            accentSoft: isDark ? '#0f2e2b' : '#ecfdf5',
            route: '/events/pricing-update-events',
        },
    ];

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ae-page ${isDark ? 'ae-dark bg-dark text-light' : 'bg-light'}`}>

                <style>{`
                    .ae-page {
                        --ae-border: ${isDark ? '#2c3242' : '#e6e8ec'};
                        --ae-bg-subtle: ${isDark ? '#1b2030' : '#f8f9fb'};
                        --ae-surface: ${isDark ? '#11151f' : '#ffffff'};
                        --ae-text: ${isDark ? '#e7e9ee' : '#1f2430'};
                        --ae-muted: ${isDark ? '#8a93a6' : '#6b7280'};
                    }
                    .ae-crumb { font-size: 12px; color: var(--ae-muted); }
                    .ae-page-title { font-size: 19px; font-weight: 700; letter-spacing: -0.01em; color: var(--ae-text); }
                    .ae-page-sub { font-size: 12.5px; color: var(--ae-muted); }

                    .ae-event-card {
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        padding: 22px;
                        border: 1px solid var(--ae-border);
                        border-radius: 12px;
                        background: var(--ae-surface);
                        cursor: pointer;
                        transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;
                    }
                    .ae-event-card:hover {
                        border-color: var(--ae-card-accent, #4f46e5);
                        box-shadow: 0 6px 20px rgba(15, 23, 42, ${isDark ? '0.35' : '0.06'});
                        transform: translateY(-2px);
                    }
                    .ae-event-card:hover .ae-event-arrow {
                        transform: translateX(3px);
                        color: var(--ae-card-accent, #4f46e5);
                    }
                    .ae-event-icon {
                        width: 44px; height: 44px;
                        display: flex; align-items: center; justify-content: center;
                        border-radius: 10px;
                        margin-bottom: 16px;
                        flex-shrink: 0;
                    }
                    .ae-event-tag {
                        position: absolute;
                        top: 22px; right: 22px;
                        font-size: 10.5px;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.06em;
                        color: var(--ae-muted);
                        padding: 3px 8px;
                        border: 1px solid var(--ae-border);
                        border-radius: 4px;
                    }
                    .ae-event-title { font-size: 15px; font-weight: 700; color: var(--ae-text); margin-bottom: 6px; }
                    .ae-event-desc { font-size: 12.5px; color: var(--ae-muted); line-height: 1.6; margin-bottom: 22px; flex-grow: 1; }
                    .ae-event-footer {
                        display: flex; align-items: center; gap: 6px;
                        font-size: 12.5px; font-weight: 600;
                        color: var(--ae-text);
                        padding-top: 14px;
                        border-top: 1px solid var(--ae-border);
                    }
                    .ae-event-arrow { transition: transform .15s ease, color .15s ease; color: var(--ae-muted); }

                    .ae-add-card {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        min-height: 180px;
                        border: 1px dashed var(--ae-border);
                        border-radius: 12px;
                        color: var(--ae-muted);
                        font-size: 12.5px;
                        font-weight: 600;
                        text-align: center;
                        padding: 22px;
                    }
                `}</style>

                {/* Page header */}
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                    <div>
                        <span className="ae-crumb">Events</span>
                        <h4 className="ae-page-title mb-1 mt-1">All Events</h4>
                        <p className="ae-page-sub mb-0">Select an event type to manage scheduled automations</p>
                    </div>
                </div>

                {/* Event type cards */}
                <div className="row g-3">
                    {eventCards.map((card, index) => {
                        const Icon = card.icon;
                        return (
                            <div className="col-12 col-md-6 col-xl-4" key={index}>
                                <div
                                    className="ae-event-card"
                                    style={{ '--ae-card-accent': card.accent }}
                                    onClick={() => navigate(card.route)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(card.route); }}
                                >
                                    <span className="ae-event-tag">{card.tag}</span>
                                    <div className="ae-event-icon" style={{ background: card.accentSoft }}>
                                        <Icon size={20} style={{ color: card.accent }} />
                                    </div>
                                    <div className="ae-event-title">{card.title}</div>
                                    <p className="ae-event-desc">{card.description}</p>
                                    <div className="ae-event-footer">
                                        View events <ArrowRight size={13} className="ae-event-arrow" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Placeholder for future event modules */}
                    {/* <div className="col-12 col-md-6 col-xl-4">
                        <div className="ae-add-card">
                            More event types will appear here as they're added.
                        </div>
                    </div> */}
                </div>

            </div>
        </React.Fragment>
    );
};

export default AllEvents;